import type { MatchOutcome } from '@/entities/prediction';
import type { OutcomeDistribution, TotalDistribution } from '@/shared/api';

export interface OutcomePercents {
  home: number;
  draw: number;
  away: number;
}

/**
 * Счётчики Прогнозов на исход → проценты, дающие в сумме ровно 100.
 *
 * Сервер шлёт счётчики, а не проценты: процент — оформление, и правило
 * округления принадлежит тому, кто рисует. Наивный `Math.round` по каждой
 * доле даёт 99 или 101 на самых обычных числах (1/1/1 → 33+33+33 = 99),
 * и полоска либо не сходится, либо переполняется.
 *
 * Метод наибольшего остатка: каждой доле достаётся целая часть, а
 * оставшиеся до сотни пункты раздаются тем, у кого дробный хвост длиннее.
 * При равных хвостах порядок фиксирован — хозяева, ничья, гости, — чтобы
 * одни и те же счётчики всегда давали одну и ту же картинку.
 *
 * Пустое распределение отдаёт нули, а не делит на ноль: показывать его
 * всё равно нечего, и решает это вызывающий.
 */
export function toPercents(distribution: OutcomeDistribution): OutcomePercents {
  const { home, draw, away, total } = distribution;
  if (total <= 0) return { home: 0, draw: 0, away: 0 };

  const parts = [
    { key: 'home' as const, exact: (home * 100) / total },
    { key: 'draw' as const, exact: (draw * 100) / total },
    { key: 'away' as const, exact: (away * 100) / total },
  ];

  const result: OutcomePercents = { home: 0, draw: 0, away: 0 };
  for (const part of parts) result[part.key] = Math.floor(part.exact);

  let left = 100 - (result.home + result.draw + result.away);
  const byRemainder = [...parts].sort(
    (a, b) => (b.exact % 1) - (a.exact % 1),
  );
  for (const part of byRemainder) {
    if (left <= 0) break;
    result[part.key] += 1;
    left -= 1;
  }
  return result;
}

/**
 * Исход, который выбрало больше всех, — его доля подсвечивается зелёным.
 *
 * Сравниваются **счётчики, а не проценты**: проценты округлены, и при
 * 1/1/1 они выходят 34/33/33 — «лидер» там появился бы из правила
 * раздачи остатка, а не из данных.
 *
 * При ничьей лидера нет — `null`, и зелёного в полосе не будет вовсе.
 * Подсветить обоих значило бы назвать лидером двоих, а выбрать одного —
 * соврать о равенстве. На малых числах ничья обычная вещь: три первых
 * Прогноза на разные исходы дают её сразу.
 */
export function leaderOf(
  distribution: OutcomeDistribution,
): MatchOutcome | null {
  const { home, draw, away, total } = distribution;
  if (total <= 0) return null;

  const max = Math.max(home, draw, away);
  const leaders: MatchOutcome[] = [];
  if (home === max) leaders.push('HOME');
  if (draw === max) leaders.push('DRAW');
  if (away === max) leaders.push('AWAY');

  return leaders.length === 1 ? (leaders[0] ?? null) : null;
}

export interface TotalPercents {
  over: number;
  under: number;
}

/**
 * То же самое для TOTAL_OVER_UNDER, но на два счётчика вместо трёх:
 * при двух долях метод наибольшего остатка вырождается в «одна доля
 * округляется вниз, вторая — это остаток до 100» — оба варианта дают
 * сумму 100 без отдельного правила разбора остатков.
 */
export function toTotalPercents(
  distribution: TotalDistribution,
): TotalPercents {
  const { over, total } = distribution;
  if (total <= 0) return { over: 0, under: 0 };

  const overPercent = Math.round((over * 100) / total);
  return { over: overPercent, under: 100 - overPercent };
}

/** Аналог leaderOf для двух долей — та же логика, «ничья» даёт null. */
export function totalLeaderOf(
  distribution: TotalDistribution,
): 'OVER' | 'UNDER' | null {
  const { over, under, total } = distribution;
  if (total <= 0 || over === under) return null;
  return over > under ? 'OVER' : 'UNDER';
}
